/**
 * StudentKvkkTab.jsx
 * KVKK silme talebi durumunu gosterir.
 * Islemler: Silme Talebini Iptal Et | Hemen Sil (destructive)
 */
import { useLanguage } from '../../contexts/LanguageContext';
import { ShieldAlert, Clock, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

function daysLeft(isoDate) {
  if (!isoDate) return null;
  return Math.ceil((new Date(isoDate) - new Date()) / (1000 * 60 * 60 * 24));
}

export default function StudentKvkkTab({ detail, onCancelDeletion, onDeleteAccount }) {
  const { t, lang } = useLanguage();

  const user = detail?.user || {};
  const isPending = !!user.is_deletion_pending;
  const scheduledAt = user.deletion_scheduled_at;
  const graceUntil = user.deletion_grace_until;
  const days = daysLeft(graceUntil);
  const isUrgent = days !== null && days <= 3;

  if (!isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle size={24} className="text-green-500" />
        </div>
        <p className="font-bold text-[var(--text-main)]">{t('admin.studentHub.kvkk_no_pending')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Status card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border-2 p-4 ${isUrgent ? 'border-rose-300 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}
      >
        <div className="flex items-center gap-3 mb-3">
          <ShieldAlert size={20} className={isUrgent ? 'text-rose-600' : 'text-amber-600'} />
          <span className={`font-bold text-sm ${isUrgent ? 'text-rose-700' : 'text-amber-700'}`}>
            {t('admin.studentHub.kvkk_pending_label')}
          </span>
        </div>
        <div className="space-y-1.5 text-xs">
          {scheduledAt && (
            <div className="flex justify-between">
              <span className="text-[var(--text-muted)]">{t('admin.studentHub.kvkk_scheduled')}</span>
              <span className="font-bold">
                {new Date(scheduledAt).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
              </span>
            </div>
          )}
          {graceUntil && (
            <div className="flex justify-between">
              <span className="text-[var(--text-muted)]">{t('admin.studentHub.kvkk_grace')}</span>
              <span className={`font-bold flex items-center gap-1 ${isUrgent ? 'text-rose-600' : ''}`}>
                <Clock size={10} />
                {days !== null
                  ? days > 0
                    ? `${days} ${lang === 'tr' ? 'gün kaldı' : 'days left'}`
                    : (lang === 'tr' ? 'Süresi doldu' : 'Grace expired')
                  : new Date(graceUntil).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
              </span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Backend note */}
      <div className="rounded-xl border-2 border-[var(--border-light)] bg-gray-50 p-3 text-xs text-[var(--text-muted)]">
        <p className="font-bold mb-1">{lang === 'tr' ? 'Backend Notu' : 'Backend Note'}</p>
        <p>
          {lang === 'tr'
            ? 'Silme iptali icin PUT /admin/students/{id}/profile allowlist\'ine is_deletion_pending, deletion_scheduled_at, deletion_grace_until alanlarinin eklenmesi gerekiyor.'
            : 'Cancel deletion requires backend PUT /admin/students/{id}/profile allowlist to include is_deletion_pending, deletion_scheduled_at, deletion_grace_until.'}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          onClick={onCancelDeletion}
          className="w-full px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold border-b-4 border-emerald-700 hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
          data-testid="kvkk-cancel-deletion-btn"
        >
          <ShieldAlert size={14} />
          {t('admin.studentHub.kvkk_cancel_btn')}
        </button>

        <button
          onClick={onDeleteAccount}
          className="w-full px-4 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-bold border-b-4 border-rose-700 hover:bg-rose-600 transition-colors"
          data-testid="kvkk-delete-now-btn"
        >
          {t('admin.studentHub.kvkk_delete_btn')}
        </button>
      </div>
    </div>
  );
}
